import React, { Fragment, useMemo, useState } from "react";
import { DIAS_SEMANA_JS, BRL0, MIL, pad2, dataLocal, diaSemanaSeg } from "../formato";

/**
 * Mapa de calor do mês (modelo do dono): pedidos por dia × hora com o esperado
 * de cada célula. Cor = % do pico DAQUELE dia (alta ≥85%, mediana ≥50%, baixa
 * ≥30%, muito baixa abaixo); célula com menos de 5 pedidos não fica verde/azul.
 * Começa mostrando só hoje; "Mostrar o mês inteiro" abre todos os dias, e
 * clicar num dia abre a linha de faturamento por hora dele.
 */
const MIN_VERDE = 5;
const NOME_FAIXA = { alta: "alta", media: "mediana", baixa: "baixa", mbaixa: "muito baixa" };

function abertoNaHora(horarios, wdSeg, h) {
  if (!horarios?.length) return true; // sem horário cadastrado: nada fica "fechado"
  const dia = horarios.find((x) => x.day_of_week === wdSeg);
  if (!dia || !dia.is_open) return false;
  const ini = `${pad2(h)}:00`, fim = `${pad2(h)}:59`;
  const dentro = (a, b) => a && b && fim >= a && ini < b;
  return dentro(dia.opening_time, dia.closing_time)
    || (dia.has_second_period && dentro(dia.opening_time_2, dia.closing_time_2))
    || (dia.has_third_period && dentro(dia.opening_time_3, dia.closing_time_3));
}

function montar(mes, horarios) {
  const base = mes.hoje.slice(0, 8), nHoje = Number(mes.hoje.slice(8, 10));
  const linhas = [];
  for (let n = 1; n <= mes.dias_no_mes; n++) {
    const iso = base + pad2(n), wd = diaSemanaSeg(iso), dia = mes.dias[n - 1];
    const cells = Array.from({ length: 24 }, (_, h) => {
      const e = mes.esperado_por_hora[wd][h];
      const exp = Math.round(e.pedidos), expFat = e.faturamento;
      const real = dia?.horas[h] || { pedidos: 0, faturamento: 0 };
      const futuro = n > nHoje || (n === nHoje && h > mes.hora_agora);
      const closed = !abertoNaHora(horarios, wd, h) && !(real.pedidos > 0);
      if (closed) return { closed: true, exp: 0, expFat: 0 };
      if (futuro) return { estado: "fut", sold: null, fat: null, exp, expFat };
      return { estado: n === nHoje && h === mes.hora_agora ? "agora" : "ok", sold: real.pedidos, fat: real.faturamento, exp, expFat };
    });
    linhas.push({ n, iso, wd, label: DIAS_SEMANA_JS[dataLocal(iso).getDay()], data: `${pad2(n)}/${base.slice(5, 7)}`, hoje: n === nHoje, futuro: n > nHoje, cells });
  }
  const cols = [];
  for (let h = 0; h < 24; h++) {
    const usada = linhas.some((r) => (r.cells[h].sold > 0) || (r.cells[h].exp > 0 && !r.cells[h].closed));
    if (usada) cols.push({ tipo: "h", h });
    else {
      const ult = cols[cols.length - 1];
      if (ult && ult.tipo === "gap" && ult.ate === h) ult.ate = h + 1;
      else cols.push({ tipo: "gap", de: h, ate: h + 1 });
    }
  }
  const picos = linhas.map((r) => Math.max(0, ...r.cells.filter((c) => c.estado === "ok").map((c) => c.sold)));
  return { linhas, cols, picos, nHoje };
}

function faixa(sold, exp, pico) {
  if (!sold && !exp) return ["zero", 0, false];
  const r = pico ? sold / pico : 0;
  let f = r >= 0.85 ? "alta" : r >= 0.5 ? "media" : r >= 0.3 ? "baixa" : "mbaixa";
  let lim = false;
  if (sold < MIN_VERDE && (f === "alta" || f === "media")) { f = "baixa"; lim = true; }
  return [f, r, lim];
}

const Seta = ({ v, e }) => (!e && !v ? null : v > e ? <span className="up">▲</span> : v < e ? <span className="dn">▼</span> : <span>=</span>);
const varTxt = (v, e) => (e ? `${Math.round((v / e - 1) * 100) >= 0 ? "+" : ""}${Math.round((v / e - 1) * 100)}%` : "–");

export default function MapaDeCalor({ mes, horarios }) {
  const { linhas, cols, picos, nHoje } = useMemo(() => montar(mes, horarios), [mes, horarios]);
  const iHoje = nHoje - 1;
  const [aberto, setAberto] = useState(false);
  const [diaAberto, setDiaAberto] = useState(iHoje);
  const [dica, setDica] = useState(null);

  const vis = linhas.map((r, i) => ({ r, i })).filter(({ i }) => aberto || i === iHoje);
  const nSeps = vis.filter(({ r }, vi) => vi > 0 && r.wd === 0).length;
  const temHora = vis.some(({ i }) => i === diaAberto);
  const nHoras = cols.filter((c) => c.tipo === "h").length;
  const mostrar = (e, titulo, sub, itens) => {
    const ret = e.currentTarget.getBoundingClientRect();
    setDica({ x: ret.left + ret.width / 2, y: ret.top, titulo, sub, itens });
  };
  const evDica = (titulo, sub, itens) => ({ onMouseEnter: (e) => mostrar(e, titulo, sub, itens), onMouseLeave: () => setDica(null) });

  let primeira = true;
  return (
    <>
      <div className="vg-scroller">
        <table className="vg-heat" onClick={() => setDica(null)}>
          <thead>
            <tr>
              <th className="rl" />
              {cols.map((c) => (c.tipo === "h"
                ? <th key={c.h}>{pad2(c.h)}h</th>
                : <th key={`g${c.de}`} title={`${pad2(c.de)}h a ${pad2(c.ate - 1)}h sem vendas`}>···</th>))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {vis.map(({ r, i }, vi) => {
              const linha = (
                <Fragment key={r.iso}>
                  {vi > 0 && r.wd === 0 && <tr className="sep" aria-hidden="true"><th className="rl" /><td colSpan={nHoras + 1} /></tr>}
                  <tr className={[r.hoje && "hoje", aberto && "clicavel", i === diaAberto && "sel"].filter(Boolean).join(" ")}
                    onClick={() => aberto && setDiaAberto(diaAberto === i ? -1 : i)}>
                    <th className="rl" scope="row">{r.label} <small>{r.data}</small>{r.hoje && <span className="tag">hoje</span>}</th>
                    {celulas(r, i)}
                    {totalDoDia(r)}
                  </tr>
                  {i === diaAberto && linhaHora(r)}
                </Fragment>
              );
              primeira = false;
              return linha;
            })}
            {linhaTotal()}
            <tr className="barra">
              <td colSpan={cols.length + 2}>
                <button type="button" aria-expanded={aberto} onClick={(e) => { e.stopPropagation(); setAberto(!aberto); if (aberto) setDiaAberto(iHoje); }}>
                  <span>{aberto ? "▴" : "▾"}</span>
                  <span><b>{aberto ? "Recolher para hoje" : "Mostrar o mês inteiro"}</b>
                    <small>{aberto ? "Mostra só o dia de hoje" : `${linhas[0].data} a ${linhas[linhas.length - 1].data}`}</small></span>
                  <span>{aberto ? "▴" : "▾"}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {dica && (
        <div className="vg-tip fixed" style={{ left: Math.max(8, Math.min(window.innerWidth - 220, dica.x - 105)), top: Math.max(8, dica.y - 12 - 34 - dica.itens.length * 22) }}>
          <h4>{dica.titulo} <small>{dica.sub}</small></h4>
          {dica.itens.map(([k, v, cls]) => <div key={k} className={cls || ""}><span>{k}</span><b>{v}</b></div>)}
        </div>
      )}
    </>
  );

  function celulas(r, i) {
    const out = [];
    for (let ci = 0; ci < cols.length; ci++) {
      const c = cols[ci];
      if (c.tipo === "gap") {
        if (primeira) {
          out.push(
            <td key={`g${c.de}`} className="gap" rowSpan={vis.length + nSeps + 1 + (temHora ? 1 : 0)}>
              <span>{pad2(c.de)}h a {pad2(c.ate - 1)}h, sem vendas</span>
            </td>);
        }
        continue;
      }
      const cell = r.cells[c.h];
      const hr = `${pad2(c.h)}h às ${pad2((c.h + 1) % 24)}h`, dia = `${r.label} ${r.data}`;
      if (cell.closed) {
        let span = 1;
        while (ci + span < cols.length && cols[ci + span].tipo === "h" && r.cells[cols[ci + span].h].closed) span++;
        const ate = cols[ci + span - 1].h;
        out.push(<td key={c.h} className="closed" colSpan={span}>Fechado {pad2(c.h)}h{ate !== c.h ? ` a ${pad2(ate)}h` : ""}</td>);
        ci += span - 1;
        continue;
      }
      if (cell.estado === "fut") {
        out.push(<td key={c.h} className="c fut" {...evDica(hr, `${dia}, ainda não aconteceu`, [["Pedidos esperados", cell.exp]])}><b>·</b><i>{cell.exp}</i></td>);
        continue;
      }
      if (cell.estado === "agora") {
        out.push(<td key={c.h} className="c agora" {...evDica(hr, `${dia}, hora em andamento`, [["Pedidos até agora", cell.sold], ["Esperado", cell.exp]])}><b>{cell.sold}</b><i>{cell.exp}</i></td>);
        continue;
      }
      const [f, rr, lim] = faixa(cell.sold, cell.exp, picos[i]);
      const itens = [["Pedidos", cell.sold], ["Esperado", cell.exp], ["Vs. esperado", varTxt(cell.sold, cell.exp)]];
      if (f !== "zero") itens.push(["Faixa do dia", `${Math.round(rr * 100)}% do pico (${NOME_FAIXA[f]})${lim ? `, menos de ${MIN_VERDE} pedidos` : ""}`]);
      out.push(<td key={c.h} className={`c ${f}`} {...evDica(hr, dia, itens)}><b>{cell.sold}</b><i><Seta v={cell.sold} e={cell.exp} />{cell.exp}</i></td>);
    }
    return out;
  }

  function totalDoDia(r) {
    let v = 0, eOc = 0, eDia = 0, ocorreu = false;
    r.cells.forEach((c) => { if (c.closed) return; eDia += c.exp; if (c.sold != null) { v += c.sold; eOc += c.exp; ocorreu = true; } });
    if (!ocorreu) return <td className="tot" {...evDica("Total do dia", `${r.data}, ainda não começou`, [["Esperado no dia", eDia]])}><b>·</b><i>{eDia}</i></td>;
    const itens = [["Pedidos", v], [`Esperado${eOc !== eDia ? " até agora" : ""}`, eOc], ["Vs. esperado", varTxt(v, eOc)]];
    if (eOc !== eDia) itens.push(["Esperado no dia todo", eDia]);
    return <td className="tot" {...evDica("Total do dia", r.data, itens)}><b>{v}</b><i><Seta v={v} e={eOc} />{eOc}</i></td>;
  }

  function linhaHora(r) {
    const horasVis = cols.filter((c) => c.tipo === "h").map((c) => c.h);
    const mx = Math.max(1, ...horasVis.map((h) => Math.max(r.cells[h].fat || 0, r.cells[h].expFat || 0)));
    const tf = r.cells.reduce((a, c) => a + (c.fat || 0), 0), te = r.cells.reduce((a, c) => a + (c.closed ? 0 : c.expFat), 0);
    return (
      <tr className="hora">
        <th className="rl"><span className="block text-[12px] font-semibold">Faturamento<small>por hora</small></span></th>
        {horasVis.map((h) => {
          const c = r.cells[h];
          if (c.closed) return <td key={h} className="hb-cell"><div className="hb" /></td>;
          const hr = `${pad2(h)}h às ${pad2((h + 1) % 24)}h`;
          const itens = c.fat != null
            ? [[`Vendido${c.estado === "agora" ? " até agora" : ""}`, BRL0(c.fat)], ["Esperado", BRL0(c.expFat)], ["Vs. esperado", varTxt(c.fat, c.expFat)]]
            : [["Esperado", BRL0(c.expFat)]];
          return (
            <td key={h} className="hb-cell" {...evDica(hr, c.fat != null ? `${r.label} ${r.data}` : "ainda não aconteceu", itens)}>
              <div className="hb">
                <i className="exp" style={{ height: `${Math.round((c.expFat / mx) * 100)}%` }} />
                {c.fat != null && <i className="sold" style={{ height: `${Math.round((c.fat / mx) * 100)}%`, opacity: c.estado === "agora" ? 0.6 : 1 }} />}
              </div>
            </td>
          );
        })}
        <td className="hb-tot" {...evDica("Faturamento do dia", r.data, [["Vendido", BRL0(tf)], ["Esperado", BRL0(te)], ["Vs. esperado", varTxt(tf, te)]])}>
          <b>{MIL(tf)}</b><i><Seta v={tf} e={te} />{MIL(te)}</i>
        </td>
      </tr>
    );
  }

  function linhaTotal() {
    let gv = 0, ge = 0;
    const tds = cols.filter((c) => c.tipo === "h").map((c) => {
      let v = 0, e = 0, eF = 0, oc = false, abertaH = false;
      vis.forEach(({ r }) => { const cell = r.cells[c.h]; if (cell.closed) return; abertaH = true; if (cell.sold != null) { v += cell.sold; e += cell.exp; oc = true; } else eF += cell.exp; });
      gv += v; ge += e;
      const fx = `${pad2(c.h)}h-${pad2((c.h + 1) % 24)}h`;
      if (!abertaH) return <td key={c.h} className="tot" />;
      if (!oc) return <td key={c.h} className="tot" {...evDica("Total do horário", `${fx}, ainda não aconteceu`, [["Esperado", eF]])}><b>·</b><i>{eF}</i></td>;
      return <td key={c.h} className="tot" {...evDica("Total do horário", `${fx}, dias exibidos`, [["Pedidos", v], ["Esperado", e], ["Vs. esperado", varTxt(v, e)]])}><b>{v}</b><i><Seta v={v} e={e} />{e}</i></td>;
    });
    return (
      <tr className="linhatot">
        <th className="rl" scope="row">Total</th>
        {tds}
        <td className="tot" {...evDica("Total geral", "dias exibidos", [["Pedidos", gv], ["Esperado", ge], ["Vs. esperado", varTxt(gv, ge)]])}><b>{gv}</b><i><Seta v={gv} e={ge} />{ge}</i></td>
      </tr>
    );
  }
}
